import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import {
  MEDIA_SUBJECT_GROUP_CLASS,
  MEDIA_SUBJECT_LIST_CLASS,
  MEDIA_SUBJECT_LIST_MAX_HEIGHT,
  MEDIA_SUBJECT_RADIO_CLASS,
  MEDIA_SUBJECT_ROW_CLASS,
  MEDIA_SUBJECT_SEARCH_SKELETON_CLASS,
  MEDIA_SUBJECT_SKELETON_LONG_ROW_COUNT,
  MEDIA_SUBJECT_SKELETON_SHORT_ROW_COUNT,
} from 'modules/championship/ui/components/form-parts/media/media-subject-picker-metrics';
import { MediaSubjectType } from 'modules/manager/domain/media-kind/media-kind-catalog';
import React from 'react';
import Label from 'ud-ui/components/label';
import { UDSkeletonInline, UDSkeletonText } from 'ud-ui/components/skeleton';
import UDText from 'ud-ui/components/text';

interface MediaSubjectPickerSkeletonProps {
  subjectType: MediaSubjectType;
  /** Вторая строка у короткого списка — число шаблонов в окне смены набора. */
  withMeta?: boolean;
}

/**
 * Скелетон списка выбора в окне «Сгенерировать» (IMGM-1508).
 *
 * Размер зависит от типа объекта: матчей почти всегда больше, чем влезает, поэтому для `match` —
 * поле поиска и список во всю высоту `MEDIA_SUBJECT_LIST_MAX_HEIGHT`; туры, этапы и группы —
 * короткий список без поиска. Так окно не меняет высоту, когда скелетон сменяется списком.
 * Строки — те же классы, что у `MediaSubjectPicker` (`media-subject-picker-metrics.ts`).
 */
const MediaSubjectPickerSkeleton = ({ subjectType, withMeta = false }: MediaSubjectPickerSkeletonProps) => {
  const { t } = useLingui();
  const isLong = subjectType === 'match';
  const rowCount = isLong ? MEDIA_SUBJECT_SKELETON_LONG_ROW_COUNT : MEDIA_SUBJECT_SKELETON_SHORT_ROW_COUNT;

  return (
    <div className="flex w-full flex-col gap-3" data-testid="media-subject-skeleton" data-subject-type={subjectType} aria-busy="true">
      {isLong && (
        // Подпись поля — настоящая: статичный текст скелетона не плашка (`.claude/docs/skeleton-kit.md`).
        <Label text={t`Поиск`}>
          <div className={MEDIA_SUBJECT_SEARCH_SKELETON_CLASS} aria-hidden data-testid="media-subject-skeleton-search" />
        </Label>
      )}
      <div
        // Строк с запасом: длинный список, как и настоящий, прокручивается внутри рамки.
        className={MEDIA_SUBJECT_LIST_CLASS}
        style={isLong ? { height: MEDIA_SUBJECT_LIST_MAX_HEIGHT } : undefined}
        aria-hidden
      >
        {/* Матчи сгруппированы по турам: список начинается с заголовка тура. */}
        {isLong && (
          <UDText type="footnote" className={MEDIA_SUBJECT_GROUP_CLASS} data-testid="media-subject-skeleton-group">
            <UDSkeletonInline className="w-16" />
          </UDText>
        )}
        {Array.from({ length: rowCount }, (_, index) => (
          <div key={index} className={MEDIA_SUBJECT_ROW_CLASS} data-testid="media-subject-skeleton-row">
            <span className={classNames(MEDIA_SUBJECT_RADIO_CLASS, 'skeleton-block rounded-pill')} />
            <span className="flex min-w-0 flex-1 flex-col">
              <UDSkeletonText type="subhead" className={isLong ? 'w-56' : 'w-32'} />
              {/* У матча под командами — дата, у набора шаблонов — их число. */}
              {(isLong || withMeta) && <UDSkeletonText type="footnote" className={isLong ? 'w-28' : 'w-20'} />}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MediaSubjectPickerSkeleton;
